//#region types
import { MonthInputProps, NumberInputProps } from "models/propTypes/wintFormPropsTypes";
import { FuelBenefitSectionProps } from "models/propTypes/wintSalaryPropTypes";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import React from "react";
import { Grid } from "@mui/material";
import { NumberInput, VerticalFormField } from "components/common/wintForms";
import { SimpleAccordionTable } from "components/common";
import MonthInput from "components/common/wintForms/formFields/monthInput";
import SalaryDeviationHelper from "./salaryDeviationHelper";
//#endregion
import { CurrencyHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

// Display only. Same floor formula as backend calculator; backend recomputes on save.
const getPrivateShare = (fuel?: SalaryDeviationFormTypes.FuelBenefitCreate): number | undefined => {
  const cost = SalaryDeviationHelper.toOptionalNumber(fuel?.fuelCost);
  const privateDistance = SalaryDeviationHelper.toOptionalNumber(fuel?.privateDistance);
  if (cost === undefined || privateDistance === undefined) return undefined;

  const total = privateDistance + (SalaryDeviationHelper.toOptionalNumber(fuel?.businessDistance) ?? 0);
  if (total <= 0) return undefined;

  // Round off float noise first: 300 × 0.1 / 0.3 = 99.999… would floor to 99, backend decimal says 100.
  return Math.floor(Math.round(((cost * privateDistance) / total) * 1e6) / 1e6);
};

const FuelBenefitSection: React.FC<FuelBenefitSectionProps> = (props) => {
  const { connect, values, readOnly } = props;

  // One key for unit: swap to type's unit when backend gets one.
  const unit = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.FuelBenefitDistanceUnit);
  const privateShare = getPrivateShare(values.fuelBenefit);

  return (
    <>
      <VerticalFormField<MonthInputProps>
        Component={MonthInput}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Month)}
        {...connect("fuelBenefit.yearMonth")}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.SalaryDeviation.CompensationMonthDescription
        )}
        showMainLabel
        disabled={readOnly}
      />
      <VerticalFormField<NumberInputProps>
        Component={NumberInput}
        {...connect("fuelBenefit.fuelCost")}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.FuelBenefitCost)}
        icon={IconDefinitionConstants.WMoney}
        suffix="kr"
        decimalPoints={2}
        allowNegative={false}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.SalaryDeviation.FuelBenefitCostDescription
        )}
        showMainLabel
        disabled={readOnly}
      />
      <VerticalFormField<NumberInputProps>
        Component={NumberInput}
        {...connect("fuelBenefit.privateDistance")}
        label={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.SalaryDeviation.FuelBenefitPrivateDistance
        )}
        suffix={unit}
        decimalPoints={2}
        allowNegative={false}
        showMainLabel
        disabled={readOnly}
      />
      <VerticalFormField<NumberInputProps>
        Component={NumberInput}
        {...connect("fuelBenefit.businessDistance")}
        label={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.SalaryDeviation.FuelBenefitBusinessDistance
        )}
        suffix={unit}
        decimalPoints={2}
        allowNegative={false}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.SalaryDeviation.FuelBenefitBusinessDistanceDescription
        )}
        showMainLabel
        disabled={readOnly}
      />
      {privateShare !== undefined && (
        <Grid container item className={cssExports.fuelBenefitPrivateShare}>
          <SimpleAccordionTable
            variant="secondary"
            items={[
              {
                title: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.SalaryDeviation.FuelBenefitPrivateShare
                ),
                value: CurrencyHelper.getFormattedCurrencyInSEK(privateShare),
                tooltip: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.SalaryDeviation.FuelBenefitPrivateShareDescription
                ),
              },
            ]}
          />
        </Grid>
      )}
    </>
  );
};

export default FuelBenefitSection;
